import React, { useMemo } from "react";
import { Filter, Eye, RotateCcw } from "lucide-react";

/**
 * WebFilterPanel — granular threshold filter with two modes.
 *
 *   filter mode    — non-matching nodes are removed
 *   highlight mode — non-matching nodes are dimmed (default)
 *
 * The user can drive each metric (spend, roas, ctr, hookRate) with a
 * min/max pair, or click a preset chip ("High ROAS", "Low ROAS", …) to
 * snap to a saved threshold.
 *
 * Returns:
 *   { dimmedIds: Set<string>, filterIds: Set<string> }
 *
 * Compute happens inside the component via the helper exported below
 * (computeMatches), so consumers can call it directly when they prefer
 * not to render this UI.
 */

export const PRESETS = [
  { key: "high_roas",       label: "High ROAS",            metric: "roas",     pred: (v) => v >= 3 },
  { key: "low_roas",        label: "Low ROAS",             metric: "roas",     pred: (v) => v < 1.5 && v > 0 },
  { key: "high_ctr",        label: "High CTR",             metric: "ctr",      pred: (v) => v >= 2 },
  { key: "low_ctr",         label: "Low CTR",              metric: "ctr",      pred: (v) => v < 0.8 && v > 0 },
  { key: "high_spend",      label: "High spend",           metric: "spend",    pred: (v) => v >= 5000 },
  { key: "low_spend",       label: "Low spend",            metric: "spend",    pred: (v) => v < 500 && v > 0 },
  { key: "high_hook",       label: "Hook lands",           metric: "hookRate", pred: (v) => v >= 0.3 },
  { key: "leverage",        label: "Leverage · low spend, high ROAS", metric: "compound" },
  { key: "fatigued",        label: "Fatigued · high spend, low ROAS", metric: "compound" },
];

export const DEFAULT_THRESHOLDS = {
  spend:    { min: 0,     max: Infinity },
  roas:     { min: 0,     max: Infinity },
  ctr:      { min: 0,     max: Infinity },
  hookRate: { min: 0,     max: Infinity },
};

export function computeMatches(nodes, thresholds) {
  const matchSet = new Set();
  nodes.forEach((n) => {
    // Hooks + unexplored bypass threshold filtering — they have different metrics
    if (n.type === "hook" || n.status === "unexplored") {
      matchSet.add(n.id);
      return;
    }
    const spend = n.spend || 0;
    const roas = n.roas || 0;
    const ctr = n.ctr || 0;
    const hr = n.hookRate || 0;
    if (
      spend >= thresholds.spend.min &&
      spend <= thresholds.spend.max &&
      roas >= thresholds.roas.min &&
      roas <= thresholds.roas.max &&
      ctr >= thresholds.ctr.min &&
      ctr <= thresholds.ctr.max &&
      hr >= thresholds.hookRate.min &&
      hr <= thresholds.hookRate.max
    ) {
      matchSet.add(n.id);
    }
  });
  return matchSet;
}

const fmt = {
  spend:    (v) => (v === Infinity ? "∞" : `$${v.toLocaleString()}`),
  roas:     (v) => (v === Infinity ? "∞" : `${v.toFixed(1)}×`),
  ctr:      (v) => (v === Infinity ? "∞" : `${v.toFixed(1)}%`),
  hookRate: (v) => (v === Infinity ? "∞" : `${Math.round(v * 100)}%`),
};

const RANGES = {
  spend:    { lo: 0, hi: 50000, step: 100 },
  roas:     { lo: 0, hi: 8,     step: 0.1 },
  ctr:      { lo: 0, hi: 5,     step: 0.05 },
  hookRate: { lo: 0, hi: 1,     step: 0.01 },
};

export default function WebFilterPanel({
  open,
  onClose,
  thresholds,
  setThresholds,
  mode,
  setMode,
  nodes,
}) {
  const matchCount = useMemo(
    () => computeMatches(nodes, thresholds).size,
    [nodes, thresholds],
  );
  const totalEligible = useMemo(
    () =>
      nodes.filter(
        (n) => n.type !== "hook" && n.status !== "unexplored",
      ).length,
    [nodes],
  );

  const update = (metric, side, value) => {
    setThresholds({
      ...thresholds,
      [metric]: { ...thresholds[metric], [side]: value },
    });
  };

  const reset = () => setThresholds(DEFAULT_THRESHOLDS);

  const applyPreset = (preset) => {
    const next = { ...DEFAULT_THRESHOLDS };
    if (preset.key === "high_roas")
      next.roas = { min: 3, max: Infinity };
    if (preset.key === "low_roas")
      next.roas = { min: 0.01, max: 1.5 };
    if (preset.key === "high_ctr")
      next.ctr = { min: 2, max: Infinity };
    if (preset.key === "low_ctr")
      next.ctr = { min: 0.01, max: 0.8 };
    if (preset.key === "high_spend")
      next.spend = { min: 5000, max: Infinity };
    if (preset.key === "low_spend")
      next.spend = { min: 0.01, max: 500 };
    if (preset.key === "high_hook")
      next.hookRate = { min: 0.3, max: Infinity };
    if (preset.key === "leverage") {
      next.spend = { min: 0.01, max: 800 };
      next.roas = { min: 3, max: Infinity };
    }
    if (preset.key === "fatigued") {
      next.spend = { min: 3000, max: Infinity };
      next.roas = { min: 0.01, max: 1.8 };
    }
    setThresholds(next);
  };

  if (!open) return null;

  return (
    <div className="web-filter-panel">
      <div className="web-filter-panel__head">
        <span className="web-filter-panel__title">Granular filter</span>
        <div className="web-filter-panel__mode">
          <button
            type="button"
            className="web-filter-panel__mode-opt"
            data-active={mode === "highlight"}
            onClick={() => setMode("highlight")}
            title="Dim non-matching nodes; keep them visible"
          >
            <Eye size={12} /> Highlight
          </button>
          <button
            type="button"
            className="web-filter-panel__mode-opt"
            data-active={mode === "filter"}
            onClick={() => setMode("filter")}
            title="Hide non-matching nodes"
          >
            <Filter size={12} /> Filter
          </button>
        </div>
        <button
          type="button"
          className="web-filter-panel__reset"
          onClick={reset}
          title="Reset all thresholds"
        >
          <RotateCcw size={12} /> Reset
        </button>
        <button
          type="button"
          className="web-filter-panel__close"
          onClick={onClose}
          aria-label="Close filter panel"
        >
          ×
        </button>
      </div>

      <div className="web-filter-panel__presets">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="web-chip"
            onClick={() => applyPreset(p)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="web-filter-panel__sliders">
        {(["spend", "roas", "ctr", "hookRate"]).map((metric) => (
          <RangeRow
            key={metric}
            metric={metric}
            label={
              metric === "hookRate" ? "Hook rate" : metric.charAt(0).toUpperCase() + metric.slice(1)
            }
            range={RANGES[metric]}
            value={thresholds[metric]}
            onChange={(side, value) => update(metric, side, value)}
          />
        ))}
      </div>

      <div className="web-filter-panel__summary">
        {mode === "highlight" ? "Highlighting" : "Filtering to"}{" "}
        <b>{matchCount}</b> of {totalEligible} eligible creative
        {totalEligible === 1 ? "" : "s"}.
      </div>
    </div>
  );
}

const RangeRow = ({ metric, label, range, value, onChange }) => {
  const minVal = value.min === 0 ? range.lo : value.min;
  const maxVal = value.max === Infinity ? range.hi : value.max;
  const f = fmt[metric];
  return (
    <div className="web-filter-row">
      <div className="web-filter-row__head">
        <span className="web-filter-row__label">{label}</span>
        <span className="web-filter-row__values">
          {f(minVal)} <span style={{ opacity: 0.5 }}>—</span> {f(maxVal === range.hi ? Infinity : maxVal)}
        </span>
      </div>
      <div className="web-filter-row__sliders">
        <input
          type="range"
          min={range.lo}
          max={range.hi}
          step={range.step}
          value={minVal}
          onChange={(e) => onChange("min", Number(e.target.value))}
        />
        <input
          type="range"
          min={range.lo}
          max={range.hi}
          step={range.step}
          value={maxVal}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange("max", v >= range.hi ? Infinity : v);
          }}
        />
      </div>
    </div>
  );
};
